/* Microphone dialog: permission, live test, device, sensitivity and headphone settings. */
import { useEffect, useRef, useState } from 'react';
import { getMicError, micDevices, startMic, stopMic, useMicFrame, useMicState } from '../core/mic';
import { setSetting, useSettings } from '../core/store';
import { noteName } from '../core/theory';
import { fmtDec } from '../core/util';
import { Btn } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { MicLevelMeter } from '../ui/widgets';
import { useGo } from './navigation';
import { closeMicSetup } from './uiState';

function LiveReadout() {
  const note = useRef<HTMLDivElement>(null);
  const detail = useRef<HTMLDivElement>(null);
  useMicFrame((f) => {
    if (f.voiced && f.midi != null) {
      const m = Math.round(f.midi);
      const c = Math.round((f.midi - m) * 100);
      if (note.current) note.current.textContent = noteName(m);
      if (detail.current) detail.current.textContent = `${fmtDec(f.freq, 1)} Hz · ${c >= 0 ? '+' : ''}${c} sent`;
    } else if (f.gated && detail.current) detail.current.textContent = 'Uygulama çalarken dinleme duraklar';
  });
  return (
    <div className="ms-live">
      <div className="ms-note" ref={note}>
        —
      </div>
      <div className="ms-detail" ref={detail}>
        Bir nota çal ya da söyle
      </div>
      <MicLevelMeter />
    </div>
  );
}

function MicOn({ onTuner }: { onTuner: () => void }) {
  const settings = useSettings();
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  useEffect(() => {
    let alive = true;
    micDevices().then((d) => {
      if (alive) setDevices(d);
    });
    return () => {
      alive = false;
    };
  }, []);
  return (
    <>
      <LiveReadout />
      <label className="field">
        <span>Mikrofon</span>
        <select
          id="mic-device"
          value={settings.micDevice}
          onChange={async (e) => {
            setSetting('micDevice', e.target.value);
            stopMic();
            await startMic();
          }}
        >
          {devices.map((d, i) => (
            <option key={d.deviceId || i} value={d.deviceId}>
              {d.label || `Mikrofon ${i + 1}`}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Hassasiyet</span>
        <input
          id="mic-sens"
          type="range"
          min="0.5"
          max="2"
          step="0.1"
          value={settings.micSensitivity}
          onChange={(e) => setSetting('micSensitivity', Number.parseFloat(e.target.value))}
        />
        <small>Sessiz ortamda düşük, gürültülü ortamda yüksek değer dene. Çubuktaki çizgi eşiği gösterir.</small>
      </label>
      <label className="check">
        <input
          id="mic-hp"
          type="checkbox"
          checked={settings.headphones}
          onChange={(e) => setSetting('headphones', e.target.checked)}
        />
        <span>Kulaklık kullanıyorum</span>
        <small>Açıkken uygulama çalarken de dinlemeye devam eder.</small>
      </label>
      <ul className="ms-tips">
        <li>Gitarı mikrofona 30–60 cm uzaklıkta tut.</li>
        <li>Başlamadan önce akort aletiyle gitarını akort et.</li>
        <li>Algılama zorlanırsa notaları biraz daha net ve tek tek çal.</li>
      </ul>
      <div className="ms-actions">
        <Btn kind="ghost" icon="micoff" onClick={stopMic}>
          Mikrofonu kapat
        </Btn>
        <Btn kind="ghost" icon="tuner" onClick={onTuner}>
          Akort aletini aç
        </Btn>
      </div>
    </>
  );
}

function MicOff() {
  const st = useMicState();
  return (
    <>
      <p>
        Uygulama çaldığın ve söylediğin notaları mikrofonla dinler. Ses kaydedilmez ve hiçbir yere gönderilmez;
        tarayıcında anlık analiz edilip silinir.
      </p>
      {st === 'denied' ? (
        <p className="t-bad">İzin reddedildi. Adres çubuğundaki mikrofon simgesinden izin verip tekrar dene.</p>
      ) : null}
      {st === 'unavailable' ? (
        <p className="t-bad">
          Bu tarayıcı ya da bağlantı mikrofona izin vermiyor. Sayfayı HTTPS üzerinden ya da yerel dosya olarak
          Chrome/Edge ile aç.
        </p>
      ) : null}
      {st === 'nodevice' ? <p className="t-bad">Bağlı bir mikrofon bulunamadı.</p> : null}
      {st === 'error' ? <p className="t-bad">Mikrofon açılamadı: {getMicError()?.message || ''}</p> : null}
      <div className="ms-actions">
        <Btn kind="primary" icon="mic" onClick={() => startMic()}>
          Mikrofonu aç
        </Btn>
      </div>
    </>
  );
}

export function MicSetupDialog() {
  const st = useMicState();
  const go = useGo();
  const [closeNow, setCloseNow] = useState(false);
  if (closeNow) return null;
  return (
    <Modal title="Mikrofon" onClose={closeMicSetup}>
      <div className="mic-setup">
        {st === 'on' ? (
          <MicOn
            onTuner={() => {
              setCloseNow(true);
              closeMicSetup();
              go('araclar.akort');
            }}
          />
        ) : (
          <MicOff />
        )}
      </div>
    </Modal>
  );
}
